"use client";

import { useTranslations } from "next-intl";
import { InfiniteScroll } from "@/components/core/InfiniteScroll/InfiniteScroll";
import { StarRating } from "@/components/ui/star-rating";
import { useProductReviews } from "@/hooks/product/useProductReviews";
import { ReviewCard } from "./ReviewCard";
import { ReviewsSkeleton } from "./ReviewsSkeleton";

interface ProductReviewsProps {
  productId: string;
}

export const ProductReviews: React.FC<ProductReviewsProps> = ({
  productId,
}) => {
  const t = useTranslations("shop");
  const query = useProductReviews(productId);
  const { reviews, summary, isLoading } = query;

  if (isLoading && reviews.length === 0) return <ReviewsSkeleton />;

  return (
    <div className="py-4 space-y-6">
      {summary.count > 0 && (
        <div className="flex items-center gap-3">
          <span className="text-2xl font-bold text-text-primary">
            {summary.average.toLocaleString("fa-IR")}
          </span>
          <StarRating value={summary.average} readonly size="md" />
          <span className="text-sm text-text-secondary">
            {t("reviews.count", { count: summary.count })}
          </span>
        </div>
      )}

      {reviews.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-semibold text-text-primary">
            {t("reviews.heading")}
          </p>
          {reviews.map((r) => (
            <ReviewCard
              key={r.id}
              rating={r.rating}
              title={r.title}
              body={r.body}
              createdAt={r.created_at}
              author={r.author}
            />
          ))}
        </div>
      )}

      {reviews.length === 0 && (
        <p className="text-sm text-text-secondary">{t("reviews.empty")}</p>
      )}
      <InfiniteScroll query={query} />
    </div>
  );
};
